/**
 * Les lignes de la session qui naissent d'une génération, et l'historique un niveau au-dessus (Wajih, 04/11) :
 *  - **la ligne vivante** (`progress`) : la génération avance étape par étape, le tuteur sent que ça travaille ;
 *  - **la carte « prêtes »** (`ready`) : courte et actionnable, la relecture se fait au centre ; survoler une
 *    proposition en montre l'aperçu au centre (web) ;
 *  - **« <champion> propose »** (`proposal`) : une activité déjà générée, à voir ou à envoyer d'un geste — la seule
 *    ligne signée du champion ;
 *  - **une activité de l'élève** (`activity`) : sa progression, son score, son temps, et ses traces dessous.
 *
 * Lues par `<TutorSessionEntry>` ; elles ne vivent pas seules.
 */
import React, { useEffect, useRef, useState } from "react"
import { Animated, Easing, Image, Platform, Pressable, View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { formatMinutesSeconds, formatTime } from "../../../ports/primary/ui/formatters/dateFormatter"
import { type UITutorGenerationStep, type UITutorSessionEntry } from "../../../ports/primary/ui/types/tutorLive"
import { CHAMPIONS, ChampionAvatar, type ChampionName } from "../../atoms/Avatar/ChampionAvatar"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { NotificationDot } from "../../atoms/NotificationDot"
import { ProgressBar } from "../../atoms/ProgressBar"
import { Reveal } from "../../atoms/Reveal"
import { Text } from "../../atoms/typography/Text"
import { useReduceMotion } from "../../hooks/useReduceMotion"
import { useSecondClock } from "../../hooks/useSecondClock"
import { AuroraFill } from "../../molecules/AuroraFill"
import { HighlightFrame } from "../../molecules/HighlightFrame"
import { shadowStyle } from "../../utils/shadow"

type Entry<K extends UITutorSessionEntry["kind"]> = Extract<UITutorSessionEntry, { kind: K }>

const TEXTS = {
    review: "Revoir",
    reviewing: "En relecture au centre",
    proposes: (helper: string) => `${helper} propose`,
    see: "Voir",
    sent: "Envoyée",
    dismiss: "Écarter la proposition",
    traces: (count: number) => `${count} étape${count > 1 ? "s" : ""}`,
    peek: (title: string) => `${title}, la voir au centre`,
}

const THUMB = 40
const STEP = 16

/** La génération qui avance : d'où elle part, ce qu'elle reprend, ce qui s'écrit ; les secondes depuis l'envoi. */
export function GenerationProgress(props: { entry: Entry<"progress">; testID?: string }) {
    const { entry, testID } = props
    const startedAt = Date.parse(entry.startedAt)
    const now = useSecondClock(Number.isNaN(startedAt) ? null : startedAt)
    const seconds = Number.isNaN(startedAt) ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000))
    return (
        <View style={styles.progress} testID={testID} accessibilityLiveRegion="polite">
            <AuroraFill radius={16} />
            <Stack gap="3">
                <Row gap="3" align="center">
                    {entry.thumbUri != null && <Image source={{ uri: entry.thumbUri }} style={styles.thumb} />}
                    <Stack gap="0" style={styles.grow}>
                        <Text size="text-sm" weight="bold" color="text-strong">
                            {entry.title}
                        </Text>
                        <Text size="text-xs" color="text-muted">
                            {formatMinutesSeconds(seconds)}
                        </Text>
                    </Stack>
                </Row>
                <Stack gap="2">
                    {entry.steps.map((step) => (
                        <Row key={step.label} gap="2" align="center">
                            <StepMark state={step.state} />
                            <Text
                                size="text-sm"
                                weight={step.state === "active" ? "semibold" : "regular"}
                                color={step.state === "todo" ? "text-muted" : "text-strong"}
                                style={styles.grow}
                            >
                                {step.label}
                            </Text>
                        </Row>
                    ))}
                </Stack>
                <Shimmer />
            </Stack>
        </View>
    )
}

/** Une étape : faite (cochée), en cours (elle respire), à venir (un cercle). */
function StepMark(props: { state: UITutorGenerationStep["state"] }) {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const pulse = useRef(new Animated.Value(1)).current
    useEffect(() => {
        if (props.state !== "active" || reduceMotion) return undefined
        const half = (toValue: number) =>
            Animated.timing(pulse, {
                toValue,
                duration: theme.motion.entree,
                easing: Easing.bezier(...theme.motion.easing),
                useNativeDriver: Platform.OS !== "web",
            })
        const loop = Animated.loop(Animated.sequence([half(0.35), half(1)]))
        loop.start()
        return () => loop.stop()
    }, [props.state, pulse, reduceMotion, theme.motion])
    if (props.state === "done") {
        return (
            <View style={styles.stepDone}>
                <Icon name="check" size={10} color="white" />
            </View>
        )
    }
    if (props.state === "active") {
        // L'opacité passe sur une vue nue : le style Unistyles reste sur la vue autour (contrat du DS).
        return (
            <Animated.View style={{ opacity: pulse }}>
                <View style={styles.stepActive} />
            </Animated.View>
        )
    }
    return <View style={styles.stepTodo} />
}

/** Une barre qui file sous les étapes : la durée n'est pas connue, le mouvement dit qu'elle court. */
function Shimmer() {
    const { theme } = useUnistyles()
    const reduceMotion = useReduceMotion()
    const x = useRef(new Animated.Value(0)).current
    useEffect(() => {
        if (reduceMotion) return undefined
        const loop = Animated.loop(
            Animated.timing(x, {
                toValue: 1,
                duration: theme.motion.entree * 3,
                easing: Easing.bezier(...theme.motion.easing),
                useNativeDriver: false,
            })
        )
        loop.start()
        return () => loop.stop()
    }, [x, reduceMotion, theme.motion])
    if (reduceMotion) return null
    const left = x.interpolate({ inputRange: [0, 1], outputRange: ["-35%", "100%"] })
    return (
        <View style={styles.shimmerTrack}>
            <Animated.View style={[styles.shimmerBar, { left }]} />
        </View>
    )
}

/** « 3 propositions prêtes » : courte, actionnable ; la relecture se fait au centre. */
export function GenerationReady(props: {
    entry: Entry<"ready">
    onReview?: () => void
    onSendFirst?: () => void
    onPeek?: (proposalId: string | null) => void
    peekId?: string | null
    testID?: string
}) {
    const { entry, onReview, onSendFirst, onPeek, peekId, testID } = props
    const reviewing = entry.reviewing === true
    const firstReady = entry.proposals.find((proposal) => proposal.status === "ready")
    const card = (
        <View style={styles.card} testID={testID}>
            <Stack gap="3">
                <Row gap="3" align="center">
                    <View style={styles.readyIcon}>
                        <Icon name="magic" size={16} color="text-on-role" />
                    </View>
                    <Stack gap="0" style={styles.grow}>
                        <Text size="text-md" weight="bold" color="text-strong">
                            {entry.title}
                        </Text>
                        <Text size="text-xs" color="text-muted">
                            {entry.caption}
                        </Text>
                    </Stack>
                    {entry.unseen != null && <NotificationDot color="danger" size={10} />}
                </Row>
                <Stack gap="1">
                    {entry.proposals.map((proposal, position) => (
                        <Pressable
                            key={proposal.id}
                            onHoverIn={proposal.status === "ready" ? () => onPeek?.(proposal.id) : undefined}
                            onHoverOut={() => onPeek?.(null)}
                            onPress={onReview}
                            disabled={onReview == null}
                            accessibilityRole="button"
                            accessibilityLabel={TEXTS.peek(proposal.title)}
                            style={styles.proposalRow(proposal.id === peekId)}
                        >
                            <Text size="text-xs" weight="bold" color="text-muted" style={styles.rowNumber}>
                                {String(position + 1)}
                            </Text>
                            <Stack gap="0" style={styles.grow}>
                                {proposal.label !== "" && (
                                    <Text size="text-xxs" weight="bold" color="role-strong" uppercase>
                                        {proposal.label}
                                    </Text>
                                )}
                                <Text
                                    size="text-sm"
                                    color={proposal.status === "ready" ? "text-strong" : "text-muted"}
                                    numberOfLines={1}
                                >
                                    {proposal.title}
                                </Text>
                            </Stack>
                            {proposal.id === peekId && <Icon name="eye" size={12} color="role" />}
                        </Pressable>
                    ))}
                </Stack>
                {entry.unseen != null && (
                    <Text size="text-xs" color="text-muted">
                        {entry.unseen}
                    </Text>
                )}
                {reviewing ? (
                    <Row gap="2" align="center" style={styles.reviewing}>
                        <Icon name="eye" size={12} color="role" />
                        <Text size="text-sm" weight="semibold" color="role-strong">
                            {TEXTS.reviewing}
                        </Text>
                    </Row>
                ) : (
                    <Row gap="2" align="center" style={styles.wrap}>
                        {onReview != null && (
                            <Button label={TEXTS.review} iconName="expand-alt" size="sm" onPress={onReview} />
                        )}
                        {onSendFirst != null && firstReady != null && entry.sendLabel != null && (
                            <Button label={entry.sendLabel} variant="ghost" size="sm" onPress={onSendFirst} />
                        )}
                    </Row>
                )}
            </Stack>
        </View>
    )
    // Le cadre désigne la prochaine action ; en relecture, elle est au centre.
    return reviewing ? card : <HighlightFrame>{card}</HighlightFrame>
}

/** « <champion> propose » : une activité déjà prête, à voir au centre ou à envoyer d'un geste. */
export function HelperProposal(props: {
    entry: Entry<"proposal">
    helper: ChampionName
    onSee?: () => void
    onSend?: () => void
    onDismiss?: () => void
    testID?: string
}) {
    const { entry, helper, onSee, onSend, onDismiss, testID } = props
    return (
        <View style={styles.card} testID={testID}>
            <AuroraFill radius={16} />
            <Stack gap="3">
                <Row align="center" justify="between">
                    <Row gap="2" align="center" style={styles.grow}>
                        <ChampionAvatar name={helper} size={20} />
                        <Text size="text-xs" weight="bold" color="text-strong" uppercase>
                            {TEXTS.proposes(CHAMPIONS[helper].label)}
                        </Text>
                        <Text size="text-xs" color="text-muted">
                            {formatTime(entry.at)}
                        </Text>
                    </Row>
                    {onDismiss != null && entry.state === "ready" && (
                        <IconButton
                            iconName="times"
                            appearance="transparent"
                            iconColor="text-muted"
                            size={28}
                            iconSize={14}
                            onPress={onDismiss}
                            accessibilityLabel={TEXTS.dismiss}
                        />
                    )}
                </Row>
                <Text size="text-sm" weight="semibold" color="text-strong">
                    {entry.reason}
                </Text>
                <View style={styles.tile}>
                    <Row gap="2" align="center">
                        <Icon name="bolt" size={12} color="success" />
                        <Text size="text-xs" weight="bold" color="text-success">
                            {entry.meta}
                        </Text>
                    </Row>
                    <Text size="text-sm" weight="semibold" color="text-strong">
                        {entry.title}
                    </Text>
                </View>
                {entry.state === "sent" ? (
                    <Row gap="2" align="center">
                        <Icon name="check-circle" size={16} color="success" />
                        <Text size="text-sm" weight="semibold" color="text-success">
                            {TEXTS.sent}
                        </Text>
                    </Row>
                ) : (
                    <Row gap="2" align="center" style={styles.wrap}>
                        {onSee != null && (
                            <Button label={TEXTS.see} iconName="eye" variant="outline" size="sm" onPress={onSee} />
                        )}
                        {onSend != null && (
                            <Button
                                label={entry.sendLabel}
                                iconName="telegram-plane"
                                size="sm"
                                loading={entry.state === "sending"}
                                onPress={onSend}
                            />
                        )}
                    </Row>
                )}
                {entry.validWhile != null && (
                    <Text size="text-xs" color="text-muted">
                        {entry.validWhile}
                    </Text>
                )}
            </Stack>
        </View>
    )
}

/** Le ton du score : la couleur de sa chip. */
const SCORE_VARIANT = { success: "success", warning: "warning", danger: "danger", neutral: "neutral" } as const

/** Une activité de l'élève, et ses traces rangées dessous ; celle qu'il a devant lui s'ouvre d'emblée. */
export function ActivityGroup(props: { entry: Entry<"activity">; testID?: string }) {
    const { entry, testID } = props
    const current = entry.status === "current"
    // État d'interface : le groupe déplié.
    const [open, setOpen] = useState(current)
    return (
        <View style={styles.activity(current)} testID={testID}>
            <Pressable onPress={() => setOpen(!open)} accessibilityRole="button" aria-expanded={open}>
                <Stack gap="2">
                    <Row gap="2" align="center">
                        <Icon
                            name={current ? "play" : entry.status === "done" ? "check-circle" : "clock"}
                            size={14}
                            color={current ? "role" : entry.status === "done" ? "success" : "text-muted"}
                        />
                        <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1} style={styles.grow}>
                            {entry.title}
                        </Text>
                        {entry.time != null && (
                            <Text size="text-xs" color="text-muted">
                                {entry.time}
                            </Text>
                        )}
                        <Icon name={open ? "chevron-up" : "chevron-down"} size={12} color="text-muted" />
                    </Row>
                    <Row gap="2" align="center">
                        {entry.progress != null && (
                            <ProgressBar
                                value={entry.progress}
                                height={6}
                                color={current ? "role" : "success"}
                                style={styles.grow}
                            />
                        )}
                        {entry.score != null ? (
                            <Badge text={entry.score.text} variant={SCORE_VARIANT[entry.score.tone]} tinted />
                        ) : (
                            <Text size="text-xs" color="text-muted">
                                {TEXTS.traces(entry.traces.length)}
                            </Text>
                        )}
                    </Row>
                </Stack>
            </Pressable>
            <Reveal open={open}>
                <Stack gap="2" style={styles.traces}>
                    {entry.traces.map((trace) => (
                        <Row key={trace.id} gap="2" align="start">
                            <Text size="text-xs" color="text-muted" style={styles.time}>
                                {formatTime(trace.at)}
                            </Text>
                            <Text size="text-sm" color="text-muted" style={styles.grow}>
                                {trace.text}
                            </Text>
                        </Row>
                    ))}
                </Stack>
            </Reveal>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    grow: { flex: 1, minWidth: 0 },
    wrap: { flexWrap: "wrap" as const },
    time: { width: 36 },
    progress: {
        padding: theme.spacing["4"],
        borderRadius: theme.radius.lg,
        backgroundColor: theme.colors["surface-raised"],
        overflow: "hidden" as const,
        ...shadowStyle(theme, "card"),
    },
    thumb: { width: THUMB, height: THUMB, borderRadius: theme.radius.sm },
    stepDone: {
        width: STEP,
        height: STEP,
        borderRadius: STEP / 2,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors.role,
    },
    stepActive: {
        width: STEP,
        height: STEP,
        borderRadius: STEP / 2,
        borderWidth: 4,
        borderColor: theme.colors.role,
        backgroundColor: theme.colors.surface,
    },
    stepTodo: {
        width: STEP,
        height: STEP,
        borderRadius: STEP / 2,
        borderWidth: 1.5,
        borderColor: theme.colors.border,
    },
    shimmerTrack: {
        height: 4,
        borderRadius: theme.radius.full,
        overflow: "hidden" as const,
        backgroundColor: theme.colors["surface-subtle"],
    },
    shimmerBar: {
        position: "absolute" as const,
        top: 0,
        bottom: 0,
        width: "35%",
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors.role,
    },
    card: {
        padding: theme.spacing["4"],
        borderRadius: theme.radius.lg,
        backgroundColor: theme.colors["surface-raised"],
        overflow: "hidden" as const,
        ...shadowStyle(theme, "card"),
    },
    readyIcon: {
        width: 34,
        height: 34,
        borderRadius: theme.radius.md,
        alignItems: "center" as const,
        justifyContent: "center" as const,
        backgroundColor: theme.colors["role-fill"],
    },
    proposalRow: (peeked: boolean) => ({
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        paddingHorizontal: theme.spacing["2"],
        paddingVertical: theme.spacing["1"],
        borderRadius: theme.radius.sm,
        backgroundColor: peeked ? theme.colors["surface-subtle"] : "transparent",
    }),
    rowNumber: { width: 12 },
    reviewing: {
        alignSelf: "flex-start" as const,
        paddingHorizontal: theme.spacing["3"],
        paddingVertical: theme.spacing["1"],
        borderRadius: theme.radius.full,
        backgroundColor: theme.colors["surface-subtle"],
    },
    tile: {
        gap: theme.spacing["1"],
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-raised"],
        borderWidth: 1,
        borderColor: theme.colors["border-subtle"],
    },
    activity: (current: boolean) => ({
        padding: theme.spacing["3"],
        borderRadius: theme.radius.md,
        backgroundColor: theme.colors["surface-raised"],
        borderWidth: 1,
        borderColor: current ? theme.colors.role : theme.colors["border-subtle"],
    }),
    traces: {
        marginTop: theme.spacing["3"],
        paddingTop: theme.spacing["3"],
        borderTopWidth: 1,
        borderTopColor: theme.colors["border-subtle"],
    },
}))
